import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, CircleCheck, CircleMinus, Layers } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { ResourceSwitch } from '@/components/ResourceSwitch';
import {
  ResourceList,
  type ResourceColumn,
  type ResourceFilterDef,
  type ResourceRowAction,
} from '@/components/resources/ResourceList';
import { StackStatsDialog } from '@/components/resources/StackStatsDialog';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import type { ListQueryConfig } from '@/lib/listQuery';
import { useListQuery } from '@/lib/hooks/useListQuery';
import { usePersistedColumns } from '@/lib/hooks/usePersistedColumns';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useServerList } from '@/lib/hooks/useServerList';
import type { Stack } from '@/types/stack';

// The stacks endpoint ignores `search`, so this page has no search box.
const LIST_CONFIG: ListQueryConfig = {
  filterKeys: ['instance_id', 'endpoint_id', 'type'],
  numericFilterKeys: ['instance_id', 'endpoint_id'],
  sortableColumns: ['name', 'is_active', 'created_at'],
  defaultSortBy: 'created_at',
  defaultSortOrder: 'desc',
};

const TYPE_OPTIONS = ['swarm', 'compose', 'kubernetes'].map((t) => ({ value: t, label: t }));

const SWITCH_OPTIONS = [
  { label: 'Stacks', to: '/resources/stacks' },
  { label: 'Services', to: '/resources/services' },
];

const fetchInstances = () => api.instances.list();
const fetchEndpoints = () => api.endpoints.listAll();

function createdText(iso: string): { ago: string; full: string } | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

const COLUMNS: ResourceColumn<Stack>[] = [
  { key: 'name', header: 'Name', sortable: true, title: (s) => s.name, cell: (s) => <span className="font-mono text-xs font-medium">{s.name}</span> },
  {
    key: 'type', header: 'Type',
    cell: (s) => <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">{s.type}</span>,
  },
  { key: 'instance', header: 'Instance', cell: (s) => s.instance?.name ?? '—' },
  { key: 'endpoint', header: 'Endpoint', hideBelow: 'md', cell: (s) => s.endpoint?.name ?? '—' },
  {
    key: 'is_active', header: 'Active', sortable: true,
    cell: (s) => {
      const Icon = s.is_active ? CircleCheck : CircleMinus;
      return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.is_active ? 'bg-status-success-soft text-status-success' : 'bg-status-neutral-soft text-status-neutral'}`}>
          <Icon className="size-3" aria-hidden="true" />
          {s.is_active ? 'Active' : 'Inactive'}
        </span>
      );
    },
  },
  {
    key: 'created_at', header: 'Created', sortable: true, hideBelow: 'md',
    cell: (s) => {
      const t = createdText(s.created_at);
      return t ? <time dateTime={s.created_at} title={t.full}>{t.ago}</time> : '—';
    },
  },
];
const ALL_KEYS = COLUMNS.map((c) => c.key);

export function StacksPage() {
  const { query, setFilter, setSort, setPage, setLimit, clearFilters } = useListQuery(LIST_CONFIG, { limitStorageKey: 'stacks.limit' });
  const { rows, total, loading, refreshing, error, refetch } = useServerList<Stack>('stacks', query);
  const instancesP = usePolledData(fetchInstances);
  const endpointsP = usePolledData(fetchEndpoints);
  const navigate = useNavigate();
  const [visible, toggleColumn] = usePersistedColumns('stacks.columns', ALL_KEYS, ALL_KEYS);
  // The stack stays set while the dialog closes so its content does not blank out mid-animation.
  const [stats, setStats] = useState<{ stack: Stack; open: boolean } | null>(null);

  const filters = useMemo<ResourceFilterDef[]>(
    () => [
      { key: 'instance_id', label: 'Instance', allLabel: 'All instances', options: (instancesP.data ?? []).map((i) => ({ value: String(i.id), label: i.name })) },
      { key: 'endpoint_id', label: 'Endpoint', allLabel: 'All endpoints', options: (endpointsP.data ?? []).map((e) => ({ value: String(e.id), label: e.name })) },
      { key: 'type', label: 'Type', allLabel: 'All types', options: TYPE_OPTIONS },
    ],
    [instancesP.data, endpointsP.data],
  );

  const actions = useMemo<ResourceRowAction<Stack>[]>(
    () => [
      {
        id: 'stats',
        label: (s) => `View stats for ${s.name}`,
        icon: Activity,
        enabled: () => true,
        run: (s) => setStats({ stack: s, open: true }),
      },
      {
        id: 'services',
        label: (s) => `View services of ${s.name}`,
        icon: Layers,
        enabled: () => true,
        run: (s) => navigate(`/resources/services?stack_id=${s.id}`),
      },
    ],
    [navigate],
  );

  const onPage = useCallback((p: number) => setPage(p, total), [setPage, total]);

  return (
    <div className="space-y-6">
      <PageHeader title="Stacks" description="Compose, Swarm and Kubernetes stacks on your endpoints." />
      <ResourceSwitch active="/resources/stacks" label="Stacks and Services" options={SWITCH_OPTIONS} />
      <ResourceList<Stack>
        columns={COLUMNS}
        visibleColumns={visible}
        onToggleColumn={toggleColumn}
        rows={rows}
        total={total}
        getRowKey={(s) => s.id}
        query={query}
        onFilter={setFilter}
        onSort={setSort}
        onPage={onPage}
        onLimit={setLimit}
        onClearFilters={clearFilters}
        filters={filters}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        tableLabel="Stacks"
        rowActions={actions}
        empty={{ title: 'No stacks yet', description: 'Stacks appear here after an instance syncs.' }}
      />
      <StackStatsDialog
        stack={stats?.stack ?? null}
        open={stats?.open ?? false}
        onOpenChange={(open) => setStats((prev) => (prev ? { ...prev, open } : prev))}
      />
    </div>
  );
}
